import { Badge } from '@/shared/components/Badge'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'
import React from 'react';
import { ArrowLeft, User, Mail, ToggleLeft, ToggleRight } from 'lucide-react';

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  isActive: boolean;
}

interface UserAssignment {
  userId: string;
  moduleId?: string;
  projectId?: string;
  permissions: string[];
  isActive: boolean;
}

interface UserAssignmentTableProps {
  title: string;
  users: User[];
  assignments: UserAssignment[];
  loading: boolean;
  onAssign: (userId: string) => void;
  onRemove: (userId: string) => void;
  onBack: () => void;
  type: 'module' | 'project';
}

const UserAssignmentTable: React.FC<UserAssignmentTableProps> = ({
  title,
  users,
  assignments,
  loading,
  onAssign,
  onRemove,
  onBack,
  type
}) => {
  // Crear un mapa de asignaciones por userId para búsqueda rápida
  const assignmentMap = new Map<string, UserAssignment>();
  assignments.forEach(assignment => {
    assignmentMap.set(assignment.userId, assignment);
  });

  // Obtener el estado de asignación para un usuario
  const getUserAssignmentStatus = (userId: string) => {
    const assignment = assignmentMap.get(userId);
    return {
      isAssigned: !!assignment,
      isActive: assignment?.isActive || false,
      permissions: assignment?.permissions || []
    };
  };

  const handleAssign = async (userId: string) => {
    await onAssign(userId);
  };

  const handleRemove = async (userId: string) => {
    await onRemove(userId);
  };

  const getToggleButton = (userId: string) => {
    const status = getUserAssignmentStatus(userId);
    
    if (!status.isAssigned || !status.isActive) {
      return (
        <div className="flex space-x-2">
          <button
            onClick={() => handleAssign(userId)}
            className="inline-flex items-center space-x-1 px-3 py-1 text-sm bg-[#E2F4FC] text-[#17B3EB] hover:text-blue-700 rounded-md transition-colors"
          >
            <User className="h-4 w-4" />
            <span>Asignar</span>
          </button>
        </div>
      );
    }

    return (
      <div className="flex space-x-2">
        <Badge color={status.isActive ? 'verde' : 'amarillo'}>Activo</Badge>
        {type === 'project' && (
          <button
            onClick={() => handleRemove(userId)}
            className="inline-flex items-center px-2 py-1 text-sm bg-red-100 text-red-700 hover:bg-red-200 rounded-md transition-colors"
            title="Remover asignación"
          >
            X
          </button>
        )}
      </div>
    );
  };


  if (loading) {
    return (
      <div className="bg-white shadow-sm rounded-lg border p-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <button
            onClick={onBack}
            className={estiloDeBoton('secundario')}
          >
            <ArrowLeft className="h-4 w-4" />
            Volver
          </button>
          <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <User className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Usuarios</p>
              <p className="text-2xl font-bold text-gray-900">{users.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <ToggleRight className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Usuarios Asignados</p>
              <p className="text-2xl font-bold text-gray-900">
                {assignments.filter(a => a.isActive).length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <ToggleLeft className="h-8 w-8 text-gray-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Sin Asignar</p>
              <p className="text-2xl font-bold text-gray-900">
                {users.length - assignments.length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg overflow-hidden border border-[#E5E7EB]">
        <div className="px-6 py-4 border-b border-gray-200 bg-[#FAF9FF]">
          <h2 className="text-lg font-medium text-gray-900">
            Gestión de Accesos de Usuarios
          </h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-[#E5E7EB]">
            <thead className="bg-[#FAF9FF]">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                  Usuario
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                  Correo Electrónico
                </th>

                <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                  Estado Usuario
                </th>
                <th className="px-6 py-4 text-right text-sm font-medium text-[#1B2128] tracking-wider">
                  Acceso al {type === 'module' ? 'Módulo' : 'Proyecto'}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-[#E1E1E1]">
              {users.map((user) => (
                  <tr key={user.id} className="hover:bg-[#F4F7FB] transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0">
                          <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center">
                            <span className="text-sm font-medium text-white">
                              {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user.firstName} {user.lastName}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Mail className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-900">{user.email}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge color={user.isActive ? 'verde' : 'gris'}>
                        {user.isActive ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      {getToggleButton(user.id)}
                    </td>
                  </tr>
                ))}
              
            </tbody>
          </table>
        </div>

        {users.length === 0 && (
          <div className="p-6 text-center text-gray-500">
            No hay usuarios disponibles
          </div>
        )}
      </div>
    </div>
  );
};

export default UserAssignmentTable;
